import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  MapPin,
  Bike,
  Car,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Users,
  Coffee,
  ChevronDown
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-800 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-900/30 transition-colors"
    >
      <span className="font-medium text-white pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-4 text-gray-400">
        {answer}
      </div>
    )}
  </div>
);

const melbourneFAQs = [
  {
    q: "How much can I earn delivering food in Melbourne?",
    a: "Melbourne delivery riders typically earn $18-$40 per hour depending on vehicle and location. E-bike riders in the CBD and inner suburbs average $18-$28/hr, scooter riders earn $22-$35/hr, and car drivers in outer areas can make $25-$40/hr. Peak times (lunch 11am-2pm, dinner 5-9pm) and weekends offer the best earnings."
  },
  {
    q: "What are the best suburbs for food delivery in Melbourne?",
    a: "Top earning areas include Melbourne CBD (office workers, tourists), Fitzroy and Collingwood (trendy dining, consistent orders), South Yarra and Prahran (affluent suburbs, good tips), Carlton and Brunswick (university students, diverse cuisine), and St Kilda (beachside tourism, evening demand). Inner suburbs work best for bikes due to Melbourne's flat terrain."
  },
  {
    q: "What licence do I need to deliver in Melbourne?",
    a: "For e-bikes (pedal-assist, max 250W, no throttle), no licence is required. For ANY scooter in Victoria, you must have a motorcycle licence (R or RE class) — this is the same as NSW, a car licence won't work. For car delivery, a valid driver's licence is needed. Overseas licences may be accepted temporarily but conversion at VicRoads is recommended."
  },
  {
    q: "How do I sign up for Uber Eats or DoorDash in Melbourne?",
    a: "To start delivering in Melbourne you need: valid photo ID, proof of work rights in Australia, an Australian bank account, a smartphone, and appropriate vehicle. Registration is done online through each platform's app or website. Document verification takes 1-5 days, and a police check may be required (~$49). Just Delivery helps streamline this process."
  },
  {
    q: "Is Melbourne good for delivery riders in winter?",
    a: "Melbourne's weather is unpredictable but delivery demand actually increases during bad weather as more people order in. Winter months (June-August) can be cold and wet, so waterproof gear is essential. Many riders prefer car delivery in winter, while e-bike riders earn premium rates during rain. The key is being prepared with proper clothing and equipment."
  },
  {
    q: "Can international students do food delivery in Melbourne?",
    a: "Yes, international students with valid work rights can do food delivery in Melbourne. You'll need a visa that allows work (most student visas allow up to 48 hours per fortnight during semester). Delivery work offers flexible hours that fit around study schedules. You'll need an Australian bank account and tax file number (TFN) to receive payments."
  }
];

export default function MelbourneGuide() {
  const [openFAQ, setOpenFAQ] = useState(null);

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": melbourneFAQs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6ABF4B]/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Link to="/how-to-start-delivery-australia" className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Australia Guide
          </Link>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#6ABF4B]" />
              <span className="text-[#6ABF4B] font-medium">MELBOURNE DELIVERY GUIDE 2026</span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              How to Start <span className="text-[#6ABF4B]">Delivery Work</span> in Melbourne
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-xl text-gray-400 mb-8 max-w-2xl">
              Your complete guide to food delivery in Melbourne — Australia's food capital with a thriving café culture and strong delivery demand.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/?lang=en#packages"
                className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
              >
                Get Started in Melbourne <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Melbourne Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Why Melbourne is Great for Delivery Riders</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Coffee className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Food Capital of Australia</h3>
              <p className="text-gray-400">Melbourne's world-renowned food scene means constant demand for deliveries from restaurants, cafés, and specialty food stores.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Dense Inner City</h3>
              <p className="text-gray-400">Melbourne's compact CBD and inner suburbs make for efficient deliveries with shorter distances between restaurants and customers.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Strong Student Market</h3>
              <p className="text-gray-400">With multiple major universities, Melbourne has a large student population that orders frequently, especially during study periods.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Extended Trading Hours</h3>
              <p className="text-gray-400">Melbourne's vibrant nightlife means delivery orders continue late into the night, offering more flexible working hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Areas */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Best Areas for Delivery in Melbourne</h2>
          <p className="text-gray-400 mb-8">These areas have the highest order volumes and best earning potential:</p>
          
          <div className="space-y-4">
            {[
              { area: "Melbourne CBD", desc: "High-volume area with office workers, tourists, and residents. Perfect for bike delivery." },
              { area: "Fitzroy & Collingwood", desc: "Trendy dining precinct with consistent orders from brunch through late night." },
              { area: "South Yarra & Prahran", desc: "Affluent suburbs with premium restaurants and generous tips." },
              { area: "Carlton & Brunswick", desc: "University area with student demand and diverse cuisine options." },
              { area: "St Kilda", desc: "Beachside suburb with tourist traffic and evening entertainment demand." },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#111111] border border-gray-800 rounded-xl p-5 flex items-start gap-4">
                <div className="w-8 h-8 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold text-sm">{idx + 1}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-white">{item.area}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Typical Melbourne Delivery Earnings</h2>
          <p className="text-gray-400 mb-8">Earnings vary based on vehicle type, hours worked, and delivery zones:</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">E-Bike</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$18 – $28/hr</p>
              <p className="text-gray-400 text-sm">Ideal for Melbourne's flat terrain and bike-friendly lanes. No licence needed.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Scooter</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$22 – $35/hr</p>
              <p className="text-gray-400 text-sm">Good for covering more ground. Requires motorcycle licence (R/RE) in VIC.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Car className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Car</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$25 – $40/hr</p>
              <p className="text-gray-400 text-sm">Best for outer suburbs like Glen Waverley and Dandenong. Weather-proof option.</p>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm mt-6 text-center">
            * Earnings are estimates based on typical Melbourne delivery data. Actual results vary by demand, time, and experience.
          </p>
        </div>
      </section>

      {/* Melbourne-Specific Requirements */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Melbourne-Specific Requirements</h2>
          <p className="text-gray-400 mb-8">Victoria has specific rules for delivery riders:</p>
          
          <div className="space-y-4">
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Scooter Licence Requirement</h3>
                  <p className="text-gray-400">In Victoria, you need a motorcycle licence (R or RE class) to ride ANY scooter, regardless of engine size. This is the same as NSW — a car licence is NOT sufficient.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#6ABF4B] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">E-Bike Friendly City</h3>
                  <p className="text-gray-400">Melbourne has excellent bike infrastructure. E-bikes must be pedal-assist only (250W max, no throttle). The flat terrain makes e-bike delivery very efficient.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Overseas Licence Conversion</h3>
                  <p className="text-gray-400">Visit VicRoads (60 Denmark St, Kew) to convert your overseas licence. Processing times vary — allow 2-4 weeks for conversion.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Weather Considerations</h3>
                  <p className="text-gray-400">Melbourne is known for unpredictable weather. Be prepared for rain — waterproof gear is essential. Demand often increases during bad weather.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Delivering in Melbourne?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Just Delivery helps you navigate the onboarding process for Uber Eats and DoorDash. We review your documents, guide you through requirements, and help you start earning faster.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/?lang=en#packages"
              className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-8 py-4 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
            >
              Start Your Application <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/how-to-start-delivery-australia"
              className="inline-flex items-center gap-2 bg-transparent border border-gray-600 text-white px-8 py-4 rounded-lg font-semibold hover:border-[#6ABF4B] transition-colors"
            >
              Read Full Australia Guide
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">Frequently Asked Questions</h2>
          <p className="text-gray-400 mb-8 text-center">Common questions about delivery work in Melbourne</p>
          
          <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden">
            {melbourneFAQs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Other Cities Links */}
      <section className="py-12 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold mb-6 text-center">Delivery Guides for Other Cities</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/how-to-start-delivery-sydney"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Sydney Guide
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/how-to-start-delivery-brisbane"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Brisbane Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-500 text-sm text-center">
            Just Delivery is not affiliated with Uber Eats, DoorDash, or any delivery platform. Platform approval decisions are made solely by the respective platforms. We provide onboarding guidance only.
          </p>
        </div>
      </section>
    </div>
    </>
  );
}
